// garuda - Notes (The Archive) Page

import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useNavigation } from "../context/NavigationContext";
import { TildeBg, FolderIcon, ShareIcon } from "../components/Icons";
import MarkdownRenderer from "../components/MarkdownRenderer";
import { fetchNotes } from "../services/contentService";
import { formatTimestamp, copyToClipboard } from "../utils/helpers";
import JackpotWord from "../components/JackpotWord";
import PageNavigation from "../components/PageNavigation";
import "./Notes.css";

const Notes = () => {
  const { topic: urlTopic, slug: urlSlug } = useParams();
  const { navigateTo } = useNavigation();
  const [topics, setTopics] = useState([]);
  const [selectedTopic, setSelectedTopic] = useState(null);
  const [selectedNote, setSelectedNote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredNotes, setFilteredNotes] = useState([]);
  const [copyFeedback, setCopyFeedback] = useState(false);
  const [fromUrl, setFromUrl] = useState(false);
  const [isReadingMode, setIsReadingMode] = useState(true);

  // Load content from files on mount
  useEffect(() => {
    const loadNotes = async () => {
      setLoading(true);
      const notesData = await fetchNotes();
      setTopics(notesData);
      setLoading(false);

      // If URL has topic and slug, find and select them
      if (urlTopic && urlSlug && notesData.length > 0) {
        const selectedTopicData = notesData.find((t) => t.id === urlTopic);
        if (selectedTopicData) {
          setSelectedTopic(selectedTopicData);
          setFilteredNotes(selectedTopicData.notes);
          const note = selectedTopicData.notes.find((n) => n.id === urlSlug);
          if (note) {
            setSelectedNote(note);
            setFromUrl(true); // Mark that we came from URL
          }
        }
      } else if (urlTopic && notesData.length > 0) {
        // Only topic in URL
        const selectedTopicData = notesData.find((t) => t.id === urlTopic);
        if (selectedTopicData) {
          setSelectedTopic(selectedTopicData);
          setFilteredNotes(selectedTopicData.notes);
          setFromUrl(true);
        }
      }
    };
    loadNotes();
  }, [urlTopic, urlSlug]);

  // Search functionality within selected topic
  useEffect(() => {
    if (!selectedTopic) {
      setFilteredNotes([]);
      return;
    }

    if (!searchQuery.trim()) {
      setFilteredNotes(selectedTopic.notes);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = selectedTopic.notes.filter(
      (note) =>
        note.title.toLowerCase().includes(query) ||
        (note.tags &&
          note.tags.some((tag) => tag.toLowerCase().includes(query))),
    );
    setFilteredNotes(filtered);
  }, [searchQuery, selectedTopic]);

  const handleTopicClick = (topic) => {
    setSelectedTopic(topic);
    setFilteredNotes(topic.notes);
    setSelectedNote(null);
    setSearchQuery("");
    setFromUrl(false); // Mark as internal navigation
  };

  const handleNoteClick = (note) => {
    setSelectedNote(note);
    setFromUrl(false); // Mark as internal navigation
  };

  const handleBackToTopics = () => {
    if (fromUrl) {
      // If we came from URL, navigate back to home
      window.location.href = "/";
    } else {
      // If we navigated internally, just clear state
      setSelectedTopic(null);
      setSelectedNote(null);
      setSearchQuery("");
    }
  };

  const handleBackToNotes = () => {
    if (fromUrl) {
      // If we came from URL, navigate back to home
      window.location.href = "/";
    } else {
      // If we navigated internally, just clear note
      setSelectedNote(null);
    }
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/notes/${selectedTopic.id}/${selectedNote.id}`;
    const success = await copyToClipboard(url);

    if (success) {
      setCopyFeedback(true);
      setTimeout(() => setCopyFeedback(false), 2000);
    } else {
      console.error("Failed to copy URL");
    }
  };

  const toggleReadingMode = () => {
    setIsReadingMode(!isReadingMode);
  };

  // Apply reading mode class to body
  useEffect(() => {
    if (isReadingMode) {
      document.body.classList.add("reading-mode");
    } else {
      document.body.classList.remove("reading-mode");
    }

    // Cleanup on unmount
    return () => {
      document.body.classList.remove("reading-mode");
    };
  }, [isReadingMode]);

  // Step 1: Topic List View
  if (!selectedTopic) {
    return (
      <div className="notes-container">
        <PageNavigation />
        <TildeBg opacity={0.05} />

        <div className="notes-header">
          <h1 className="page-title">
            <JackpotWord onClick={() => navigateTo("home")}>Notes</JackpotWord>
          </h1>
          <p className="page-subtitle">The archive of organized thoughts.</p>
        </div>

        <div className="topics-grid">
          {loading ? (
            <p className="no-results">Loading topics...</p>
          ) : topics.length === 0 ? (
            <p className="no-results">No topics yet.</p>
          ) : (
            topics.map((topic) => (
              <div
                key={topic.id}
                className="topic-card"
                onClick={() => handleTopicClick(topic)}
              >
                <FolderIcon className="topic-icon" />
                <h2 className="topic-name">
                  <JackpotWord>{topic.topic}</JackpotWord>
                </h2>
                <p className="topic-count">{topic.notes.length} notes</p>
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Step 2: Note List View
  if (!selectedNote) {
    return (
      <div className="notes-container">
        <PageNavigation />
        <TildeBg opacity={0.05} />

        <div className="notes-header">
          <button className="back-button" onClick={handleBackToTopics}>
            ← Back to Topics
          </button>
          <h1 className="page-title">{selectedTopic.topic}</h1>
        </div>

        {/* Search Bar */}
        <div className="search-container">
          <input
            type="text"
            className="search-input"
            placeholder="search notes_"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        <div className="notes-list">
          {filteredNotes.length === 0 ? (
            <p className="no-results">No notes found.</p>
          ) : (
            filteredNotes.map((note) => (
              <div
                key={note.id}
                className="note-item"
                onClick={() => handleNoteClick(note)}
              >
                <h3 className="note-title">
                  <JackpotWord>{note.title}</JackpotWord>
                </h3>
                {note.timestamp && (
                  <p className="note-timestamp">
                    {formatTimestamp(note.timestamp)}
                  </p>
                )}
                {note.tags && note.tags.length > 0 && (
                  <div className="note-tags">
                    {note.tags.map((tag, idx) => (
                      <span key={idx} className="tag">
                        #{tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    );
  }

  // Step 3: Note Detail View
  return (
    <div className="notes-container note-view">
      <PageNavigation
        isReadingMode={isReadingMode}
        onToggleReadingMode={toggleReadingMode}
        showReadingMode={true}
      />
      <TildeBg opacity={0.03} />

      <div className="note-detail-header">
        <button className="back-button" onClick={handleBackToNotes}>
          ← Back to {selectedTopic.topic}
        </button>
      </div>

      <article className="note-content">
        <div className="note-detail-title-row">
          <h1 className="note-detail-title">{selectedNote.title}</h1>
          <button
            className="share-button"
            onClick={handleShare}
            title="Copy link to clipboard"
          >
            <ShareIcon />
            {copyFeedback && <span className="copy-feedback">Copied!</span>}
          </button>
        </div>
        <div className="author-section">
          <img
            src="/images/profile.jpg"
            alt="Prem Agarwal"
            className="author-avatar"
            onError={(e) => {
              e.target.style.display = "none";
              e.target.nextSibling.style.display = "flex";
            }}
          />
          <div
            className="author-avatar author-avatar-fallback"
            style={{ display: "none" }}
          >
            PA
          </div>
          <div className="author-info">
            <div className="author-name">Prem Agarwal</div>
            {selectedNote.timestamp && (
              <div className="author-timestamp">
                {formatTimestamp(selectedNote.timestamp)}
              </div>
            )}
          </div>
        </div>
        {selectedNote.tags && selectedNote.tags.length > 0 && (
          <div className="note-detail-tags">
            {selectedNote.tags.map((tag, idx) => (
              <span key={idx} className="tag">
                #{tag}
              </span>
            ))}
          </div>
        )}
        <MarkdownRenderer content={selectedNote.content} />
      </article>
    </div>
  );
};

export default Notes;
